/**
 * Limpiador de Formato Markdown v1.0
 * 
 * FMT-02: Elimina asteriscos de formato Markdown que contaminan
 * nombres y direcciones, causando fallos en geocodificación.
 * 
 * Problema: "**Hospital San Juan**" → falla en API CartoCiudad
 * Solución: "**Hospital San Juan**" → "Hospital San Juan"
 * 
 * @module utils/markdownCleaner
 */

/** Resultado de limpieza */
export interface CleanResult {
  cleaned: string;
  modifications: number;
  patterns: string[];
}

/**
 * Limpia formato Markdown de un texto
 * 
 * @example
 * cleanMarkdown("**Hospital**") // → "Hospital"
 * cleanMarkdown("*Centro de Salud*") // → "Centro de Salud"
 * cleanMarkdown("C/ **Mayor**, 5") // → "C/ Mayor, 5"
 */
export function cleanMarkdown(text: string): CleanResult {
  if (!text || typeof text !== 'string') {
    return { cleaned: text || '', modifications: 0, patterns: [] };
  }
  
  let cleaned = text;
  let modifications = 0;
  const patterns: string[] = [];
  
  // 1. Eliminar **bold** (más común en PTEL)
  const boldRegex = /\*\*([^*]+)\*\*/g;
  if (boldRegex.test(cleaned)) {
    const matches = cleaned.match(boldRegex);
    cleaned = cleaned.replace(boldRegex, '$1');
    modifications += matches?.length || 0;
    patterns.push('bold');
  }
  
  // 2. Eliminar *italic* (cuidado con asteriscos sueltos)
  // Solo si está rodeado de texto, no "5 * 3" matemático
  const italicRegex = /(?<!\*)\*([^*\s][^*]*[^*\s]|[^*\s])\*(?!\*)/g;
  if (italicRegex.test(cleaned)) {
    const matches = cleaned.match(italicRegex);
    cleaned = cleaned.replace(italicRegex, '$1');
    modifications += matches?.length || 0;
    patterns.push('italic');
  }
  
  // 3. Eliminar ~~strikethrough~~
  const strikeRegex = /~~([^~]+)~~/g;
  if (strikeRegex.test(cleaned)) {
    const matches = cleaned.match(strikeRegex);
    cleaned = cleaned.replace(strikeRegex, '$1');
    modifications += matches?.length || 0;
    patterns.push('strikethrough');
  }
  
  // 4. Eliminar `code` inline
  const codeRegex = /`([^`]+)`/g;
  if (codeRegex.test(cleaned)) {
    const matches = cleaned.match(codeRegex);
    cleaned = cleaned.replace(codeRegex, '$1');
    modifications += matches?.length || 0;
    patterns.push('code');
  }
  
  // 5. Convertir [texto](url) a solo texto
  const linkRegex = /\[([^\]]+)\]\([^)]+\)/g;
  if (linkRegex.test(cleaned)) {
    const matches = cleaned.match(linkRegex);
    cleaned = cleaned.replace(linkRegex, '$1');
    modifications += matches?.length || 0;
    patterns.push('link');
  }
  
  // 6. Eliminar headers markdown al inicio (# ## ###)
  const headerRegex = /^#{1,6}\s+/gm;
  if (headerRegex.test(cleaned)) {
    cleaned = cleaned.replace(headerRegex, '');
    modifications++;
    patterns.push('header');
  }
  
  // 7. Limpiar asteriscos sueltos al inicio/final (residuos)
  // Pero NO eliminar asteriscos que son parte del texto (ej: "5*")
  if (/^\*+\s/.test(cleaned)) {
    cleaned = cleaned.replace(/^\*+\s+/, '');
    modifications++;
    patterns.push('leading-asterisks');
  }
  if (/\s\*+$/.test(cleaned)) {
    cleaned = cleaned.replace(/\s+\*+$/, '');
    modifications++;
    patterns.push('trailing-asterisks');
  }
  
  return {
    cleaned: cleaned.trim(),
    modifications,
    patterns,
  };
}

/**
 * Limpia Markdown de múltiples campos de un registro
 */
export function cleanRecordMarkdown<T extends Record<string, unknown>>(
  record: T,
  fields: (keyof T)[]
): { record: T; totalModifications: number } {
  const cleaned = { ...record };
  let totalModifications = 0;
  
  for (const field of fields) {
    const value = record[field];
    if (typeof value === 'string') {
      const result = cleanMarkdown(value);
      (cleaned as Record<string, unknown>)[field as string] = result.cleaned;
      totalModifications += result.modifications;
    }
  }
  
  return { record: cleaned, totalModifications };
}

/**
 * Limpia Markdown de un array de registros
 */
export function cleanBatchMarkdown<T extends Record<string, unknown>>(
  records: T[],
  fields: (keyof T)[]
): { records: T[]; stats: { processed: number; modified: number; totalChanges: number } } {
  let modified = 0;
  let totalChanges = 0;
  
  const cleaned = records.map(record => {
    const result = cleanRecordMarkdown(record, fields);
    if (result.totalModifications > 0) {
      modified++;
      totalChanges += result.totalModifications;
    }
    return result.record;
  });
  
  return {
    records: cleaned,
    stats: {
      processed: records.length,
      modified,
      totalChanges,
    },
  };
}

/**
 * Verifica si un texto contiene formato Markdown
 */
export function hasMarkdown(text: string): boolean {
  if (!text) return false;
  
  return /\*\*[^*]+\*\*/.test(text) ||           // **bold**
         /(?<!\*)\*[^*\s]+\*(?!\*)/.test(text) || // *italic*
         /~~[^~]+~~/.test(text) ||                // ~~strike~~
         /`[^`]+`/.test(text) ||                  // `code`
         /\[[^\]]+\]\([^)]+\)/.test(text);        // [link](url)
}

/**
 * Extrae texto limpio de dirección para geocodificación
 * Aplica limpieza Markdown + normalización básica
 */
export function cleanAddressForGeocoding(address: string): string {
  if (!address) return '';
  
  // 1. Limpiar Markdown
  let cleaned = cleanMarkdown(address).cleaned;
  
  // 2. Normalizar espacios múltiples
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  // 3. Eliminar caracteres problemáticos para APIs
  cleaned = cleaned.replace(/[""'']/g, '"'); // Comillas tipográficas
  cleaned = cleaned.replace(/[–—]/g, '-');   // Guiones largos
  
  return cleaned.trim();
}
